import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { PortfolioBrand, AttentionReason } from "@/hooks/use-portfolio";

interface Chip {
  key: AttentionReason;
  label: string;
  href: string;
}

type TFn = (key: string, opts?: Record<string, unknown>) => string;

function chipsFor(brand: PortfolioBrand, t: TFn): Chip[] {
  return brand.needsAttention.map((reason): Chip => {
    switch (reason) {
      case "autopilot_paused": {
        const r = brand.automation.autopilot.pauseReason;
        const pauseReasonLabel = r ? (t(`pause.${r}`) as string) : null;
        return {
          key: reason,
          label: pauseReasonLabel
            ? t("attention.autopilotPausedReason", { reason: pauseReasonLabel })
            : t("attention.autopilotPaused"),
          href: `/${brand.slug}/settings/automation`,
        };
      }
      case "ads_paused": {
        const r = brand.automation.ads.pauseReason;
        const pauseReasonLabel = r ? (t(`pause.${r}`) as string) : null;
        return {
          key: reason,
          label: pauseReasonLabel
            ? t("attention.adsPausedReason", { reason: pauseReasonLabel })
            : t("attention.adsPaused"),
          href: `/${brand.slug}/settings/automation`,
        };
      }
      case "failed_posts":
        return {
          key: reason,
          label: t("attention.failedPosts", { count: brand.failedPosts7d }),
          href: `/${brand.slug}/calendar`,
        };
      case "ai_budget":
        return {
          key: reason,
          label: t("attention.aiBudget", { pct: Math.round(brand.aiSpend.percentUsed * 100) }),
          href: `/${brand.slug}/settings/ai-usage`,
        };
      case "ads_budget":
        return {
          key: reason,
          label: t("attention.adsBudget", { pct: Math.round(brand.adSpend.percentUsed * 100) }),
          href: `/${brand.slug}/ads`,
        };
    }
  });
}

/** One row per brand that needs attention; renders nothing when all clear. */
export function NeedsAttentionStrip({ brands }: { brands: PortfolioBrand[] }) {
  const { t } = useTranslation("dashboard");
  const flagged = brands.filter((b) => b.needsAttention.length > 0);
  if (flagged.length === 0) return null;

  return (
    <div
      data-testid="needs-attention-strip"
      className="rounded-lg border border-amber-300/70 bg-amber-50 p-3 space-y-2 dark:border-amber-900 dark:bg-amber-950/30"
    >
      {flagged.map((b) => (
        <div key={b.id} className="flex flex-wrap items-center gap-2 text-sm">
          <AlertTriangle size={14} className="flex-shrink-0 text-amber-600 dark:text-amber-500" />
          <Link to={`/${b.slug}`} className="font-medium hover:underline">
            {b.name}
          </Link>
          {chipsFor(b, t).map((c) => (
            <Link
              key={c.key}
              to={c.href}
              className="rounded-full border border-amber-300 bg-background px-2 py-0.5 text-xs hover:bg-accent transition-colors dark:border-amber-800"
            >
              {c.label}
            </Link>
          ))}
        </div>
      ))}
    </div>
  );
}
